import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import SearchableSelect from "../../components/SearchableSelect";
import { StatusBadge, PriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import {
  getAdminTicket,
  sendAdminTicketMessage,
  updateAdminTicketStatus,
  updateAdminTicketPriority,
  assignAdminTicket,
  unassignAdminTicket,
} from "../../api/adminTicketService";
import { listEmployees } from "../../api/employeeService";
import { paginatedRows } from "../../utils/pagination";
import { formatDate, fromNow, ticketNumber, initials } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const ticket = useApi(() => getAdminTicket(id), [id, refreshKey]);
  const employees = useApi(() => listEmployees({ per_page: 100 }));
  const t = ticket.data?.data;
  const employeeRows = paginatedRows(employees.data);
  const assigneeOptions = useMemo(
    () => employeeRows.map((e) => ({ id: e.id, label: e.name, hint: e.department?.name })),
    [employeeRows],
  );

  const send = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await sendAdminTicketMessage(id, reply);
      setReply("");
      ticket.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await updateAdminTicketStatus(id, status);
      showToast("success", "Status updated");
      ticket.reload();
    } catch (err) { showApiError(err); }
  };

  const setPriority = async (priority) => {
    try {
      await updateAdminTicketPriority(id, priority);
      showToast("success", "Priority updated");
      ticket.reload();
    } catch (err) { showApiError(err); }
  };

  const setAssignee = async (employeeId) => {
    try {
      if (!employeeId) {
        await unassignAdminTicket(id);
      } else {
        await assignAdminTicket(id, employeeId);
      }
      showToast("success", "Assignment updated");
      ticket.reload();
    } catch (err) { showApiError(err); }
  };

  if (ticket.loading || !t) return <LoadingBlock rows={3} height={80} />;

  const messages = t.messages || [];

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Tickets", to: "/admin/tickets" },
          { label: ticketNumber(t.id) },
        ]}
      />
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginLeft: -8, marginBottom: 8 }}>
            <Icon name="chevron-left" size={14} /> Back
          </button>
          <h1 className="page-title">{t.subject}</h1>
          <div className="page-sub row gap-sm" style={{ marginTop: 4 }}>
            <span className="t-mono">{ticketNumber(t.id)}</span>
            <span>·</span>
            <span>opened {formatDate(t.created_at)}</span>
            <span>·</span>
            <span>by <b style={{ color: "var(--ink-800)" }}>{t.customer?.name}</b></span>
          </div>
        </div>
        <div className="page-actions">
          <PriorityBadge priority={t.priority} />
          <StatusBadge status={t.status} />
        </div>
      </div>

      <div className="grid-stack-300">
        <div className="card">
          <div className="card-body">
            <div className="ticket-thread">
              {t.description && (
                <div className="bubble customer">
                  <div className="avatar avatar-sm">{initials(t.customer?.name)}</div>
                  <div>
                    <div className="bubble-content">{t.description}</div>
                    <div className="bubble-meta">{formatDate(t.created_at)}</div>
                  </div>
                </div>
              )}
              {messages.map((m) => {
                const r = m.sender?.role;
                const right = r === "admin" || r === "user";
                const cls = r === "admin" ? "admin" : r === "customer" ? "customer" : "employee";
                return (
                  <div key={m.id} className={`bubble ${cls} ${right ? "right" : ""}`}>
                    <div className="avatar avatar-sm">{initials(m.sender?.name)}</div>
                    <div>
                      <div className="bubble-content">{m.message}</div>
                      <div className="bubble-meta" style={right ? { justifyContent: "flex-end" } : null}>
                        <span>{m.sender?.name}</span><span>·</span><span>{fromNow(m.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 24, padding: 14, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <textarea
                className="textarea"
                placeholder="Reply as admin…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                style={{ background: "white", minHeight: 80 }}
              />
              <div className="row" style={{ marginTop: 8 }}>
                <div style={{ marginLeft: "auto" }}>
                  <button className="btn btn-primary btn-sm" disabled={!reply.trim() || saving} onClick={send}>
                    <Icon name="send" size={12} /> Send reply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col gap-md">
          <div className="card">
            <div className="card-head"><div className="card-title">Assignment</div></div>
            <div className="card-body col gap-sm">
              <div className="field">
                <label className="field-label">Assigned to</label>
                {/* tickets.assigned_to stores a User id; this picker is keyed by
                    Employee id. Map the current assignee back to its employee
                    row via user_id. */}
                <SearchableSelect
                  value={
                    t.assignee?.id
                      ? employeeRows.find((e) => e.user_id === t.assignee.id)?.id ?? ""
                      : ""
                  }
                  onChange={(v) => setAssignee(v || null)}
                  options={assigneeOptions}
                  placeholder="— Unassigned —"
                  searchPlaceholder="Search employees…"
                  width="100%"
                />
                {t.assignee?.id &&
                  !employeeRows.find((e) => e.user_id === t.assignee.id) && (
                    <div className="field-hint" style={{ color: "var(--warning)" }}>
                      Currently assigned to <b>{t.assignee.name}</b> (no matching employee record).
                    </div>
                  )}
              </div>
              <div className="field">
                <label className="field-label">Priority</label>
                <select className="select" value={t.priority} onChange={(e) => setPriority(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Status</label>
                <select className="select" value={t.status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-head"><div className="card-title">Customer</div></div>
            <div className="card-body col gap-sm">
              <div className="row gap-sm">
                <div className="avatar">{initials(t.customer?.name)}</div>
                <div>
                  <div className="t-primary fw-600">{t.customer?.name}</div>
                  <div className="t-muted text-xs">{t.customer?.phone}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
